import { computed } from 'vue'
import { HDate, Sedra } from '@hebcal/core'
import portionsData from '@/data/portions.json'

export interface Portion {
	name: string
	nameHebrew: string
	verses: string
	versesHebrew: string
	book: string
	bookHebrew: string
}

const portions = portionsData as Portion[]

/**
 * Normalize a parasha name for matching between @hebcal/core output and our
 * portions.json `name` field (strip spaces, apostrophes, hyphens, lowercase).
 * e.g. "Chayei Sara" ↔ "Chayei Sarah", "Achrei Mot" ↔ "Achrei Mot".
 */
export function normalizeParashaName(s: string) {
	return s.toLowerCase().replace(/[\s'’\-.]/g, '')
}

const norm = normalizeParashaName

const portionByNorm = new Map(portions.map(p => [norm(p.name), p]))

// Spelling differences between @hebcal/core output and our portions.json.
// (Verified against a full-year sweep of hebcal's diaspora sedra names — only
// these four real parashiyot differ after normalization; the rest are festival
// Shabbatot with no weekly sidra, handled via `isHoliday`.)
const HEBCAL_ALIASES: Record<string, string> = {
	[norm('Chayei Sara')]: norm('Chayei Sarah'),
	[norm('Shmini')]: norm('Shemini'),
	[norm("Beha'alotcha")]: norm('Behaalotecha'),
	[norm("Sh'lach")]: norm('Shelach'),
}

function resolvePortion(hebcalName: string): Portion | undefined {
	const key = norm(hebcalName)
	return portionByNorm.get(key) ?? portionByNorm.get(HEBCAL_ALIASES[key] ?? '')
}

/**
 * Compute the parasha read on the upcoming (or current) Shabbat for a given date.
 * Returns the matching portions.json entries — an array to support combined
 * parashiyot (e.g. Matot-Masei) and the raw hebcal names.
 */
export function getWeeklyParashaFor(date: Date = new Date()): {
	names: string[]
	portions: Portion[]
	isHoliday: boolean
} {
	// Advance to the coming Saturday so mid-week shows the *upcoming* portion.
	const d = new Date(date)
	const day = d.getDay() // 0=Sun … 6=Sat
	const daysToSat = (6 - day + 7) % 7
	d.setDate(d.getDate() + daysToSat)

	const hd = new HDate(d)
	const sedra = new Sedra(hd.getFullYear(), false) // false = diaspora schedule
	const info = sedra.lookup(hd)

	// On a festival Shabbat there's no weekly sidra (info.parsha is empty).
	const names: string[] = Array.isArray(info.parsha) ? info.parsha : []
	const resolved = names
		.map(resolvePortion)
		.filter((p): p is Portion => !!p)

	return { names, portions: resolved, isHoliday: !!info.chag }
}

/**
 * Reactive helper: the current week's parasha (primary portion + combined list).
 */
export function useWeeklyParasha(date: Date = new Date()) {
	const result = computed(() => getWeeklyParashaFor(date))
	const primary = computed(() => result.value.portions[0] ?? null)
	const reference = computed(() =>
		primary.value ? `${primary.value.book} ${primary.value.verses}` : '',
	)
	return {
		/** All portions for the week (1, or 2 when combined). */
		portions: computed(() => result.value.portions),
		/** The primary (first) portion object. */
		primary,
		/** Raw hebcal parasha name(s). */
		names: computed(() => result.value.names),
		/** True when the coming Shabbat is a festival (no weekly sidra). */
		isHoliday: computed(() => result.value.isHoliday),
		/** e.g. "Deuteronomy 16:18-21:9" for the primary portion. */
		reference,
	}
}
